"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AdjudicationQuestionStatus = exports.AdjudicationQuestionKind = void 0;
exports.questionKeyOf = questionKeyOf;
exports.isGroundTruthQuestion = isGroundTruthQuestion;
exports.isErrorQuestion = isErrorQuestion;
exports.supersededGroundTruthOf = supersededGroundTruthOf;
exports.signalQuestionKeyOf = signalQuestionKeyOf;
exports.buildAdjudicationQuestions = buildAdjudicationQuestions;
exports.isAdjudicationComplete = isAdjudicationComplete;
const consensus_types_1 = require("../../../../Consensus/consensus.types");
const signal_name_normalization_1 = require("../../../../DataScience/signal-hygiene/signal-name-normalization");
const package_data_adjudication_types_1 = require("../../../../PackageDataAdjudication/package-data-adjudication.types");
const adjudication_roster_1 = require("./adjudication-roster");
const adjudication_step_types_1 = require("./adjudication-step.types");
const adjudication_admitted_rows_1 = require("./adjudication-admitted-rows");
const adjudication_slice_1 = require("./adjudication-slice");
const KEY_SEPARATOR = '\x1F';
var AdjudicationQuestionKind;
(function (AdjudicationQuestionKind) {
    AdjudicationQuestionKind["GroundTruthExistence"] = "ground_truth_existence";
    AdjudicationQuestionKind["GroundTruthValue"] = "ground_truth_value";
    AdjudicationQuestionKind["InferenceError"] = "inference_error";
    AdjudicationQuestionKind["SignalDisposition"] = "signal_disposition";
    AdjudicationQuestionKind["SupersededGroundTruth"] = "superseded_ground_truth";
})(AdjudicationQuestionKind || (exports.AdjudicationQuestionKind = AdjudicationQuestionKind = {}));
var AdjudicationQuestionStatus;
(function (AdjudicationQuestionStatus) {
    AdjudicationQuestionStatus["Open"] = "open";
    AdjudicationQuestionStatus["Blocked"] = "blocked";
    AdjudicationQuestionStatus["Answered"] = "answered";
    AdjudicationQuestionStatus["Moot"] = "moot";
})(AdjudicationQuestionStatus || (exports.AdjudicationQuestionStatus = AdjudicationQuestionStatus = {}));
function questionKeyOf(kind, anchor, rawValue) {
    const parts = [kind, (0, consensus_types_1.anchorKey)(anchor)];
    if (kind === AdjudicationQuestionKind.InferenceError || kind === AdjudicationQuestionKind.SupersededGroundTruth) {
        parts.push(canonicalJson(rawValue ?? null));
    }
    return parts.join(KEY_SEPARATOR);
}
function isGroundTruthQuestion(question) {
    return (question.kind === AdjudicationQuestionKind.GroundTruthValue ||
        question.kind === AdjudicationQuestionKind.GroundTruthExistence);
}
function isErrorQuestion(question) {
    return (question.kind === AdjudicationQuestionKind.InferenceError ||
        question.kind === AdjudicationQuestionKind.SupersededGroundTruth);
}
function supersededGroundTruthOf(input, standing, decision) {
    const previous = standing?.groundTruthDatum ?? null;
    if (previous === null || !(0, adjudication_step_types_1.isGroundTruthRuled)(decision)) {
        return null;
    }
    if (decision.anchor.kind === consensus_types_1.AnchorKind.Field && previous.kind === consensus_types_1.AnchorKind.Field) {
        return (0, adjudication_slice_1.sameCellValue)(input, decision.anchor, decision.rawValue, previous.value.raw) ? null : ({ rawValue: previous.value.raw });
    }
    if (decision.anchor.kind === consensus_types_1.AnchorKind.Row &&
        previous.kind === consensus_types_1.AnchorKind.Row &&
        previous.existence === package_data_adjudication_types_1.RowExistence.Present &&
        decision.existence === package_data_adjudication_types_1.RowExistence.Absent) {
        return { rawValue: null };
    }
    return null;
}
function signalQuestionKeyOf(signalName) {
    return [AdjudicationQuestionKind.SignalDisposition, (0, adjudication_roster_1.signalIdentityKey)(signalName)].join(KEY_SEPARATOR);
}
function buildAdjudicationQuestions(input, output) {
    const standingByAnchor = new Map(input.standings.map((standing) => [(0, consensus_types_1.anchorKey)(standing.anchor), standing]));
    const rowByKey = new Map(input.rows.map((row) => [row.rowKey, row]));
    const absentRowKeys = (0, adjudication_step_types_1.rowsRuledAbsent)(output);
    const synthesizedRowKeys = new Set((0, adjudication_admitted_rows_1.synthesizedRowKeysOf)(input));
    const rowQuestions = new Map();
    const questions = [];
    for (const [key, contested] of groupContestedByAnchor(input.contested)) {
        const standing = standingByAnchor.get(key) ?? null;
        if (standing?.status === package_data_adjudication_types_1.GroundTruthStandingStatus.RowRuledAbsent) {
            continue;
        }
        const anchor = contested[0].anchor;
        let blockedBy;
        if (synthesizedRowKeys.has(anchor.rowKey)) {
            if (!rowQuestions.has(anchor.rowKey)) {
                const rowQuestion = buildSynthesizedRowQuestion(input, output, anchor.rowKey, standingByAnchor);
                rowQuestions.set(anchor.rowKey, rowQuestion);
                questions.push(...rowQuestionsOf(input, rowQuestion, standingByAnchor, output));
            }
            const rowQuestion = rowQuestions.get(anchor.rowKey) ?? null;
            if (rowQuestion !== null && !isRuledPresent(output, anchor.rowKey)) {
                blockedBy = rowQuestion.questionKey;
            }
        }
        const isInAbsentRow = anchor.kind === consensus_types_1.AnchorKind.Field && absentRowKeys.has(anchor.rowKey);
        const groundTruthQuestion = buildGroundTruthQuestion(anchor, standing, contested, rowByKey, output, isInAbsentRow, blockedBy);
        if (groundTruthQuestion !== null) {
            questions.push(groundTruthQuestion);
        }
        for (const value of contested) {
            questions.push(isInAbsentRow ?
                { ...inferenceQuestionBase(value, groundTruthQuestion), status: AdjudicationQuestionStatus.Moot }
                : buildInferenceQuestion(input, value, standing, groundTruthQuestion, output));
        }
        const superseded = isInAbsentRow ? null : buildSupersededQuestion(input, anchor, standing, contested, output);
        if (superseded !== null) {
            questions.push(superseded);
        }
    }
    return [
        ...questions,
        ...buildAdmittedRowValueQuestions(input, output, standingByAnchor),
        ...buildSignalDispositionQuestions(input, output),
    ];
}
function rowQuestionsOf(input, rowQuestion, standingByAnchor, output) {
    if (rowQuestion === null) {
        return [];
    }
    const superseded = buildSupersededQuestion(input, rowQuestion.anchor, standingByAnchor.get((0, consensus_types_1.anchorKey)(rowQuestion.anchor)) ?? null, [], output);
    return superseded === null ? [rowQuestion] : [rowQuestion, superseded];
}
function buildSynthesizedRowQuestion(input, output, rowKey, standingByAnchor) {
    const anchor = { kind: consensus_types_1.AnchorKind.Row, rowKey };
    const standing = standingByAnchor.get((0, consensus_types_1.anchorKey)(anchor)) ?? null;
    if (standing?.status === package_data_adjudication_types_1.GroundTruthStandingStatus.VettedAndCurrent) {
        return null;
    }
    const proposedCells = Object.fromEntries(input.contested.flatMap((value) => (value.anchor.kind === consensus_types_1.AnchorKind.Field &&
        value.anchor.rowKey === rowKey &&
        value.proposed.kind === consensus_types_1.AnchorKind.Field) ?
        [[value.anchor.column, value.proposed.value.raw]]
        : []));
    return {
        kind: AdjudicationQuestionKind.GroundTruthExistence,
        questionKey: questionKeyOf(AdjudicationQuestionKind.GroundTruthExistence, anchor),
        anchor,
        status: (0, adjudication_step_types_1.isGroundTruthRuled)(findGroundTruthDecision(output, anchor)) ?
            AdjudicationQuestionStatus.Answered
            : AdjudicationQuestionStatus.Open,
        currentExistence: package_data_adjudication_types_1.RowExistence.Absent,
        rowContent: { ...proposedCells, ...((0, adjudication_admitted_rows_1.recoveredKeyCellsOf)(input, output, rowKey) ?? {}) },
        standing,
        direction: package_data_adjudication_types_1.DiscrepancyDirection.Extra,
    };
}
function buildAdmittedRowValueQuestions(input, output, standingByAnchor) {
    const admitted = (0, adjudication_admitted_rows_1.admittedRowKeysOf)(input, output);
    if (admitted.size === 0) {
        return [];
    }
    const slice = (0, adjudication_slice_1.adjudicationSliceOf)(input);
    const contestedKeys = new Set(input.contested.map((value) => (0, consensus_types_1.anchorKey)(value.anchor)));
    return [...admitted].flatMap((rowKey) => {
        const proposed = (0, adjudication_admitted_rows_1.proposedRowContentOf)(input, rowKey);
        return slice.valueColumnNames().flatMap((column) => {
            const anchor = { kind: consensus_types_1.AnchorKind.Field, rowKey, column };
            if (contestedKeys.has((0, consensus_types_1.anchorKey)(anchor))) {
                return [];
            }
            const standing = standingByAnchor.get((0, consensus_types_1.anchorKey)(anchor)) ?? null;
            const proposedValue = proposed?.[column];
            const question = {
                kind: AdjudicationQuestionKind.GroundTruthValue,
                questionKey: questionKeyOf(AdjudicationQuestionKind.GroundTruthValue, anchor),
                anchor,
                status: (0, adjudication_step_types_1.isGroundTruthRuled)(findGroundTruthDecision(output, anchor)) ?
                    AdjudicationQuestionStatus.Answered
                    : AdjudicationQuestionStatus.Open,
                currentValue: null,
                standing,
                suggestedValues: proposedValue === undefined || proposedValue === null ?
                    []
                    : [{ raw: proposedValue, normalized: slice.normalizeValue(column, proposedValue, proposed ?? undefined) }],
            };
            const superseded = buildSupersededQuestion(input, anchor, standing, [], output);
            return superseded === null ? [question] : [question, superseded];
        });
    });
}
function isRuledPresent(output, rowKey) {
    return findGroundTruthDecision(output, { kind: consensus_types_1.AnchorKind.Row, rowKey })?.existence === package_data_adjudication_types_1.RowExistence.Present;
}
function isAdjudicationComplete(questions) {
    return questions.every((question) => question.status === AdjudicationQuestionStatus.Answered || question.status === AdjudicationQuestionStatus.Moot);
}
function buildSignalDispositionQuestions(input, output) {
    return (0, adjudication_roster_1.introducedSignalNames)(input, output).map((introduced) => ({
        kind: AdjudicationQuestionKind.SignalDisposition,
        questionKey: signalQuestionKeyOf(introduced.name),
        anchor: introduced.anchor,
        status: (0, adjudication_roster_1.isSignalRuled)((0, adjudication_roster_1.signalDecisionForName)(output, introduced.name)) ?
            AdjudicationQuestionStatus.Answered
            : AdjudicationQuestionStatus.Open,
        signalName: introduced.name,
        renameCandidate: introduced.origin === adjudication_roster_1.IntroducedSignalOrigin.RowKey ? null : renameCandidateFor(input.roster, introduced.name),
        origin: introduced.origin,
    }));
}
function renameCandidateFor(roster, name) {
    const folded = (0, signal_name_normalization_1.foldSignalNameNotation)(name);
    if (folded === '') {
        return null;
    }
    const matches = roster.filter((signal) => (0, signal_name_normalization_1.foldSignalNameNotation)(signal.name) === folded);
    return matches.length === 1 ? matches[0] : null;
}
function groupContestedByAnchor(contested) {
    const grouped = new Map();
    for (const value of contested) {
        const key = (0, consensus_types_1.anchorKey)(value.anchor);
        const existing = grouped.get(key);
        if (existing === undefined) {
            grouped.set(key, [value]);
            continue;
        }
        existing.push(value);
    }
    return grouped;
}
function buildGroundTruthQuestion(anchor, standing, contested, rowByKey, output, isInAbsentRow, blockedBy) {
    if (standing?.status === package_data_adjudication_types_1.GroundTruthStandingStatus.VettedAndCurrent) {
        return null;
    }
    const decision = findGroundTruthDecision(output, anchor);
    const status = isInAbsentRow ? AdjudicationQuestionStatus.Moot
        : blockedBy !== undefined ? AdjudicationQuestionStatus.Blocked
            : (0, adjudication_step_types_1.isGroundTruthRuled)(decision) ? AdjudicationQuestionStatus.Answered
                : AdjudicationQuestionStatus.Open;
    if (anchor.kind === consensus_types_1.AnchorKind.Row) {
        const row = rowByKey.get(anchor.rowKey);
        const proposedContent = contested.find((value) => value.proposed.kind === consensus_types_1.AnchorKind.Row)?.proposed;
        return {
            kind: AdjudicationQuestionKind.GroundTruthExistence,
            questionKey: questionKeyOf(AdjudicationQuestionKind.GroundTruthExistence, anchor),
            anchor,
            status,
            currentExistence: row === undefined ? package_data_adjudication_types_1.RowExistence.Absent : package_data_adjudication_types_1.RowExistence.Present,
            rowContent: row?.values ??
                (proposedContent !== undefined && proposedContent.kind === consensus_types_1.AnchorKind.Row ?
                    asRecord(proposedContent.rowContentRaw)
                    : {}),
            standing,
            direction: row === undefined ? package_data_adjudication_types_1.DiscrepancyDirection.Extra : package_data_adjudication_types_1.DiscrepancyDirection.Missing,
        };
    }
    return {
        kind: AdjudicationQuestionKind.GroundTruthValue,
        questionKey: questionKeyOf(AdjudicationQuestionKind.GroundTruthValue, anchor),
        anchor,
        status,
        currentValue: rowByKey.get(anchor.rowKey)?.values[anchor.column] ?? null,
        standing,
        suggestedValues: distinctSuggestedValues(contested),
        ...(blockedBy === undefined ? {} : { blockedBy }),
    };
}
function buildInferenceQuestion(input, contested, standing, groundTruthQuestion, output) {
    const { anchor } = contested;
    const base = inferenceQuestionBase(contested, groundTruthQuestion);
    const { rawValue } = base;
    if (groundTruthQuestion !== null && groundTruthQuestion.status !== AdjudicationQuestionStatus.Answered) {
        return { ...base, status: AdjudicationQuestionStatus.Blocked };
    }
    if (settledGroundTruthMatches(input, contested, standing, findGroundTruthDecision(output, anchor))) {
        return { ...base, status: AdjudicationQuestionStatus.Moot };
    }
    const answered = (0, adjudication_step_types_1.isErrorRuled)(findErrorDecision(output, anchor, rawValue));
    return { ...base, status: answered ? AdjudicationQuestionStatus.Answered : AdjudicationQuestionStatus.Open };
}
function buildSupersededQuestion(input, anchor, standing, contested, output) {
    const superseded = supersededGroundTruthOf(input, standing, findGroundTruthDecision(output, anchor));
    if (superseded === null) {
        return null;
    }
    const asked = contested.some((value) => value.proposed.kind === consensus_types_1.AnchorKind.Row ?
        superseded.rawValue === null
        : (0, adjudication_slice_1.sameCellValue)(input, anchor, value.proposed.value.raw, superseded.rawValue));
    if (asked) {
        return null;
    }
    return {
        kind: AdjudicationQuestionKind.SupersededGroundTruth,
        questionKey: questionKeyOf(AdjudicationQuestionKind.SupersededGroundTruth, anchor, superseded.rawValue),
        anchor,
        status: (0, adjudication_step_types_1.isErrorRuled)(findErrorDecision(output, anchor, superseded.rawValue)) ?
            AdjudicationQuestionStatus.Answered
            : AdjudicationQuestionStatus.Open,
        rawValue: superseded.rawValue,
        provenance: standing?.provenance ?? null,
    };
}
function inferenceQuestionBase(contested, groundTruthQuestion) {
    const { anchor, proposed } = contested;
    const isRow = proposed.kind === consensus_types_1.AnchorKind.Row;
    const rawValue = isRow ? null : proposed.value.raw;
    return {
        kind: AdjudicationQuestionKind.InferenceError,
        questionKey: questionKeyOf(AdjudicationQuestionKind.InferenceError, anchor, rawValue),
        anchor,
        rawValue,
        proposedExistence: isRow ? proposed.existence : undefined,
        sources: [{ sourceKind: contested.sourceKind, sourceRef: contested.sourceRef }],
        blockedBy: groundTruthQuestion?.questionKey ?? null,
    };
}
function settledGroundTruthMatches(input, contested, standing, decision) {
    const { proposed } = contested;
    if (proposed.kind === consensus_types_1.AnchorKind.Row) {
        const settled = decision?.existence ??
            (standing?.groundTruthDatum?.kind === consensus_types_1.AnchorKind.Row ? standing.groundTruthDatum.existence : undefined);
        return settled !== undefined && settled === proposed.existence;
    }
    if ((0, adjudication_step_types_1.isGroundTruthRuled)(decision)) {
        return (0, adjudication_slice_1.sameCellValue)(input, contested.anchor, decision.rawValue, proposed.value.raw);
    }
    if (standing?.groundTruthDatum?.kind === consensus_types_1.AnchorKind.Field) {
        return standing.groundTruthDatum.value.normalized === proposed.value.normalized;
    }
    return false;
}
function distinctSuggestedValues(contested) {
    const seen = new Set();
    const values = [];
    for (const value of contested) {
        if (value.proposed.kind !== consensus_types_1.AnchorKind.Field) {
            continue;
        }
        const key = value.proposed.value.normalized ?? canonicalJson(value.proposed.value.raw);
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        values.push(value.proposed.value);
    }
    return values;
}
function findGroundTruthDecision(output, anchor) {
    const key = (0, consensus_types_1.anchorKey)(anchor);
    return output.groundTruthDecisions.find((decision) => (0, consensus_types_1.anchorKey)(decision.anchor) === key);
}
function findErrorDecision(output, anchor, rawValue) {
    const key = (0, consensus_types_1.anchorKey)(anchor);
    const value = canonicalJson(rawValue);
    return output.errorDecisions.find((decision) => (0, consensus_types_1.anchorKey)(decision.anchor) === key && canonicalJson(decision.rawValue) === value);
}
function asRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {};
}
function canonicalJson(value) {
    if (value === undefined) {
        return 'undefined';
    }
    return JSON.stringify(value, (_key, nested) => {
        if (typeof nested !== 'object' || nested === null || Array.isArray(nested)) {
            return nested;
        }
        const entries = Object.entries(nested).sort(([left], [right]) => left < right ? -1
            : left > right ? 1
                : 0);
        return Object.fromEntries(entries);
    });
}
//# sourceMappingURL=adjudication-questions.js.map